import React, { useEffect } from 'react'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ComponentIds } from 'src/Utils/testIds'
import {
  ShowPageItemStatsRow,
  ShowPageItemStatsLabel,
  AlignedShowPageItemStatsValue,
} from 'src/components/PageLayout/ShowPage'
import { OwnerAssignmentButton } from 'src/components/Reusable/Buttons/OwnerAssignmentButton'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import palette from 'src/designSystem/variables/palette'
import styled from 'styled-components/macro'
import formatDate from 'src/Utils/formatDate'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import log from 'loglevel'
import { RiskOwnerLabel, RiskTimestamps } from '../risks.styles'
import { RisksCategoryMenu } from '../menus/RisksCategoryMenu'
import { useCreateOrUpdateRisks, useRiskCategoryOptions } from '../risks.hooks'
import { updatedRiskRpcData } from '../risks.helpers'
import { RiskFields } from '../risks.constants'

const RiskDepartmentChip = styled(DepartmentChip)`
  height: 40px;
  border-color: ${palette.neutral[200]};
  padding: 8px 10px;
`

export const RiskLinkages = ({ risk }: { risk: Risk }): JSX.Element => {
  const createOrUpdateRisks = useCreateOrUpdateRisks()
  const initConfig = useRiskCategoryOptions()
  const riskObj = risk.toObject()
  const canUpdateDepartment = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISK_DEPARTMENT,
    riskObj,
  )
  const canUpdateOwner = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISK_OWNER,
    riskObj,
  )

  useEffect(() => {
    initConfig()
  }, [initConfig])

  if (!risk || !riskObj) {
    return <></>
  }

  const riskId = risk.getId()
  const updatedAt = formatDate(
    new Date(risk.getUpdatedAt()).toLocaleString(),
    true,
    false,
  )
  const createdAt = formatDate(
    new Date(risk.getCreatedAt()).toLocaleString(),
    true,
    false,
  )
  const ownerEmail = risk.getOwnerEmail()
  const deptId = risk.getDepartmentId()

  const updateDept = async (newDeptId: string) => {
    // TODO: Update Dept API handling
    if (!canUpdateDepartment) {
      return
    }
    try {
      const updatedRisk = updatedRiskRpcData(
        risk,
        RiskFields.departmentId,
        newDeptId,
      )
      await createOrUpdateRisks([updatedRisk])
    } catch (err) {
      log.error(`Error updating risk ${riskId} department to ${newDeptId}`, err)
    }
  }

  return (
    <>
      <ShowPageItemStatsRow>
        <ShowPageItemStatsLabel>Assignee</ShowPageItemStatsLabel>
        <AlignedShowPageItemStatsValue>
          <OwnerAssignmentButton
            id={ComponentIds.OWNER_DROPDOWN}
            model={risk}
            modelType={MODEL_TYPE.RISK}
            modelId={risk.getId()}
            email={ownerEmail}
            isDisabled={!canUpdateOwner}
          />
          <RiskOwnerLabel htmlFor={ComponentIds.OWNER_DROPDOWN}>
            {ownerEmail}
          </RiskOwnerLabel>
        </AlignedShowPageItemStatsValue>
      </ShowPageItemStatsRow>
      <ShowPageItemStatsRow>
        <ShowPageItemStatsLabel>Department</ShowPageItemStatsLabel>
        <AlignedShowPageItemStatsValue>
          <RiskDepartmentChip
            departmentId={deptId}
            onChanged={updateDept}
            allowEmpty
            isDisabled={!canUpdateDepartment}
          />
        </AlignedShowPageItemStatsValue>
      </ShowPageItemStatsRow>
      <ShowPageItemStatsRow>
        <ShowPageItemStatsLabel>Category</ShowPageItemStatsLabel>
        <AlignedShowPageItemStatsValue>
          <RisksCategoryMenu riskId={riskId} />
        </AlignedShowPageItemStatsValue>
      </ShowPageItemStatsRow>
      <RiskTimestamps>
        <p>Created: {createdAt}</p>
        <p>Last Modified: {updatedAt}</p>
      </RiskTimestamps>
    </>
  )
}
